<!--
  Also referred to as the "collage editor".
  NOTE XXX: Has a component name DIFFERENT than the file name
    e.g. ImageWidgetOld / <image-widget-old> / imageWidgetOld   (grep words)
  Held "within" an ImageBoxComponent.vue a.k.a ImageWidget
  The main drag/drop editing functionality is via, http://fabricjs.com/docs/
  mockup: https://www.figma.com/file/XVpKitt8tcAkZlV6xc3PdJ/%E2%9C%85-Master?node-id=95%3A9545

  Q. Codepath of the "Add Image" box using the "From URL" tab work?
  A.
      i. Below, <image-box-tools> is given this v-on a prop:

            @addBase64Image="$evt => addBase64Image($evt.image_url)"

      ii. addBase64Image()
     iii. uploadToS3AndReplace()
      iv. api.views.upload_media_to_s3() <stylelab-backend>/api/views.py:3551
 -->
<template>
  <main-layout
    class="image-row-container d-flex align-items-center pt-0 overlay"
    :class="{ 'loading': loading }"
    :fixed-size="false"
    @click.native.self="clickOutside"
  >
    <base-container
      class="collage-canvas"
      fluid
    >
      <template>
        <span v-hotkey="keymap" />
      </template>
      <!-- Image Row -->
      <div
        class="close-button-container"
        :class="`${showCropper ? 'close-button-container--disabled' : ''}`"
      >
        <!--
        cW: {{ canvasWidth }} &nbsp;
        cH: {{ canvasHeight }} &nbsp;
        wH: {{ windowHeight }} &nbsp;
        wW: {{ windowWidth }} &nbsp;
        zoom: {{ calcZoomRatio() }}
        A/R: {{ aspectRatio }}
        <span @click="decreaseZoomRatio">magic</span>
        -->
        <div
          class="d-flex close-button float-right"
          @click="onClose"
        >
          <base-icon
            icon="plus"
            style="transform: rotate(45deg) scale(1.3)"
          />
          <base-text class="ml-2">
            Close
          </base-text>
        </div>
      </div>
      <base-widget class="basewidget">
        <div>
          <div
            v-if="mode === 'selectBackground'"
            class="image-row__actions"
          >
            <background-color-box-tool-desktop v-click-outside="resetMode" />
          </div>

          <template v-if="!isIPad">
            <div
              v-show="showEditMenu && mode !== 'cropping' && !showCropper"
              id="editmenu"
              :key="showEditMenu"
              class="image-row__actions contextual-menu"
              :class="(isSmallCanvas && isIPad) ? 'editmenu--small' : ''"
            >
              <!-- Crop v1  -->
              <!-- <new-crop-image-box-tool
                ref="CropImageBoxTool"
                custom-classes="submenubutton"
                @changeMode="changeMode"
              /> -->
              <!-- Crop v2  -->
              <frame-crop-image-box-tool
                ref="FrameCropImageBoxTool"
                custom-classes="submenubutton"
                @change-mode="changeMode"
                @show-cropper="showCropper = true"
                @hide-cropper="showCropper = false"
              />
              <remove-background-image-box-tool
                custom-classes="submenubutton"
                @loading="loading = true"
                @finished="loading = false"
              />
              <bring-to-front-image-box-tool custom-classes="submenubutton" />
              <send-to-back-image-box-tool custom-classes="submenubutton" />
              <text-font-box-tool
                :submenu-value="submenu"
                align="top"
                @changeSubmenuValue="changeSubValue"
              />
              <text-size-box-tool
                :last-font-size="lastFontSize"
                :submenu-value="submenu"
                align="top"
                @changeSubmenuValue="changeSubValue"
              />
              <color-box-tool
                :fill-color="fillColor"
                :submenu-value="submenu"
                align="bottom"
                @selectColor="setFill"
                @changeSubmenuValue="changeSubValue"
              />
              <delete-image-box-tool
                custom-classes="submenubutton"
                :is-editing="isEditing"
                @closeDialog="closeEditMenu"
              />
            </div>

            <!-- Crop v1 Controls -->
            <!-- <div
              v-if="mode == 'cropping'"
              class="image-row__actions contextual-menu"
            >
              <button
                class="image-row__button d-flex submenubutton"
                @click="cropActions('complete')"
              >
                <base-icon
                  icon="check"
                  class="mr-2"
                />
                <div>Apply</div>
              </button>
              <button
                class="image-row__button d-flex submenubutton"
                @click="cropActions('reset')"
              >
                <base-icon
                  icon="undo"
                  class="mr-2"
                />
                <div>Reset Crop</div>
              </button>
              <button
                class="image-row__button d-flex submenubutton"
                @click="cropActions('cancel')"
              >
                <base-icon
                  icon="cancel"
                  class="mr-2"
                />
                <div>Cancel</div>
              </button>
            </div> -->
            <!-- / Crop v1 Controls -->
          </template>
          <div
            class="canvaswrap position-relative"
            :class="{...dropClasses,
                     'canvaswrap--small': isSmallCanvas && isSpecialFolder,
            }"
            :style="`height: ${containerSize.h}px; width: ${containerSize.w}px`"
            @contextmenu.prevent.stop="onShowContextMenu"
            @drop="onDrop"
            @dragover.prevent="onDragOver"
            @dragenter.prevent="onDragEnter"
            @dragleave.prevent="onDragLeave"
          >
            <transition
              name="fade"
              appear
            >
              <div
                v-if="loading"
                class="loading_spinner"
              >
                <font-awesome-icon
                  class="spinner"
                  icon="spinner"
                  size="3x"
                  spin
                />
              </div>
            </transition>
            <canvas
              :id="'editor-'+uniqid"
              ref="canvas"
              crossorigin="anonymous"
              :width="width"
              :height="height"
              :style="`border: none; box-shadow: none;`"
              class="position-absolute"
              @keyup="keymonitor"
            />
            <!-- This must go next to the canvas -->
            <frame-cropper
              class="position-absolute"
              :menu-on-bottom="isIPad"
              :show="showCropper"
              @change-mode="changeMode"
              @close="showCropper = false"
              @finished="onCropFinished"
            />
          </div>
        </div>
        <template v-slot:footer>
          <image-box-tools
            ref="tools"
            v-bind="{
              isIPad,
              mode,
              lastFontSize,
              submenu,
              isEditing,
              showEditMenu,
              fillColor
            }"
            :add-image-is-open="addImageIsOpen"
            @update:addImageIsOpen="handleUpdateAddImageIsOpen"
            @resetSelection="resetSelections"
            @imageAdded="imageAdded"
            @addBase64Image="$evt => addBase64Image($evt.image_url)"
            @canvas-added="canvasAdded"
            @loading="loading = true"
            @finished="loading = false"
            @textSelected="activateObject"
            @change="handleImageAnimated"
            @changeSubmenuValue="changeSubValue"
            @closeDialog="closeEditMenu"
            @changeMode="changeMode"
            @selectColor="setFill"
            @showBackgroundColorPicker="showBackgroundColorPicker"
            @show-cropper="showCropper = true"
            @hide-cropper="showCropper = false"
          />
          <vue-context
            ref="rightClickMenu"
            class="contextual-menu image-row__actions"
            :lazy="true"
          >
            <template v-slot:default="{data}">
              <button
                v-for="(element, index) in data"
                :key="`vuecontext_${index}`"
                class="d-flex image-row__button submenubutton"
                :class="` ${element.name === 'Paste' && !clipboardHasImage ? '--disabled' : ''} `"
                @click.prevent="element.action"
                @contextmenu.prevent="element.action"
              >
                <!-- @click.prevent="element.action" -->
                <base-icon
                  :icon="element.icon"
                  class="mr-2"
                />
                <div>{{ element.name }}</div>
              </button>
            </template>
          </vue-context>
          <!-- /Menu -->
        </template>
      </base-widget>
    </base-container>
    <!-- Add link -->
    <add-link-modal
      v-if="currentObject"
      ref="addLink"
      v-model="currentObjectUrl"
      @link-modal-changed="onLinkModalChanged"
    />
  </main-layout>
</template>

<script>
import { fabric } from 'fabric';
import FabricWithHammer from '@/_utilities/FabricWithHammer';
import 'fabric-history';
import { EventBus } from '@/event-bus';
import { VueContext } from 'vue-context';

import SendToBackImageBoxTool from '@/components/ImageBox/SendToBackImageBoxTool';
import api from '@/_api';
import PasteImageMixin from '@/_utilities/PasteImageMixin';
import Whichbrowser from '@/components/mixins/whichbrowser';
import AddLinkModal from './ImageBox/AddLinkModal';
import AddLinkMixin from './ImageBox/AddLinkMixin';
import RightClickMixin from './ImageBox/RightClickMixin';
import ImageBoxTools from './ImageBox/ImageBoxTools';
import ImageRowComponentDropMixin from './ImageRowComponentDropMixin';

import { DEFAULT_TEXT } from './ImageBox/AddTextImageBoxTool';

export default {
  name: 'ImageWidgetOld',
  components: {
    VueContext, SendToBackImageBoxTool, AddLinkModal, ImageBoxTools,
  },
  mixins: [
    PasteImageMixin,
    RightClickMixin,
    AddLinkMixin,
    Whichbrowser,
    ImageRowComponentDropMixin,
  ],
  provide() {
    return {
      parent: this.$data,
    };
  },
  props: {
    content: {
      type: Object,
      default: () => {},
    },
    aspectRatio: {
      type: String,
      default: '16:13',
      validator(value) {
        return value.match(/^(\d+):(\d+)$/g);
      },
    },
    width: {
      type: Number,
      default: 800,
    },
  },
  data() {
    return {
      // Used to display the cropper
      showCropper: false,

      blurhandler: null,
      mode: null,
      innerContent: null,
      canvas: null,
      png_render: '',
      pointer: {},
      currentObject: null,
      uniqid: Date.now(),
      isDrawingMode: 0,
      strokeColor: '#ff0000',
      fillColor: '#000000',
      // colors: ['#4D4D4D', '#999999', '#FFFFFF', '#F44E3B', '#FE9200', '#FCDC00', '#DBDF00', '#A4DD00', '#68CCCA', '#73D8FF', '#AEA1FF', '#FDA1FF', '#333333', '#808080', '#cccccc', '#D33115', '#E27300', '#FCC400', '#B0BC00', '#68BC00', '#16A5A5', '#009CE0', '#7B64FF', '#FA28FF', '#000000', '#666666', '#B3B3B3', '#9F0500', '#C45100', '#FB9E00', '#808900', '#194D33', '#0C797D', '#0062B1', '#653294', '#AB149E'],

      font: '',
      font_display_name: '',
      sizePreset: undefined,
      lastFontSize: undefined,
      sizePresets: [
        { key: 'x-small', value: 11, selectable: true },
        { key: 'Small', value: 14, selectable: true },
        { key: 'Normal', value: 18, selectable: true },
        { key: 'Large', value: 20, selectable: true },
        { key: 'Huge', value: 24, selectable: true },
        { key: 'Custom', value: null, selectable: false },
      ],

      showSelector: false,

      addImageIsOpen: false,

      step: 1,
      options: this.content.options,

      showEditMenu: false,
      showEditMenuTouched: false, // Used when displaying side menu on iPad

      submenu: null,
      loading: false,
      showLinkModal: false,

      default_text: DEFAULT_TEXT,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,

      isSmallCanvas: false,
      lastSelectionTime: new Date(),

      lastObject: null,
      clearLastObjectTimeout: null,
    };
  },

  computed: {
    // isIPad() {
    //   return true;
    // },
    containerSize() {
      const [w, h] = this.parseAspectRatio(this.aspectRatio);

      let height;
      let width;
      if (this.windowWidth > this.windowHeight) {
        // get max container height
        height = ((this.windowHeight - 48) * 0.95) - 35; // Consider bottom toolbar and scale down a bit
        // compute width
        width = Math.round((w * height) / h);
      } else {
        // get max container height
        width = Math.min(this.windowWidth - 32, 800);
        // compute width
        height = Math.round((h * width) / w) - 35;
      }

      return { h: height, w: width };
    },
    canvasScale() {
      return this.containerSize.w / this.width;
    },
    height() {
      // Compute height from aspect ratio
      const [w, h] = this.parseAspectRatio(this.aspectRatio);
      return Math.floor((this.width * h) / w);
    },
    fonts() {
      return this.$store.state.fonts.items;
    },
    currentObjectType() {
      return this.currentObject ? this.currentObject.get('type') : '';
    },
    keymap() {
      return {
        left: () => { this.moveSelection(-this.step, 0); },
        right: () => { this.moveSelection(this.step, 0); },
        up: () => { this.moveSelection(0, -this.step); },
        down: () => { this.moveSelection(0, this.step); },
        'meta+shift+down': () => { this.moveSelectedToBack(); },
        'meta+v': () => { this.handlePaste(); },
        'meta+c': () => { this.copyFabricObject(); },
        'ctrl+shift+d': () => {
          if (this.currentObject === null) {
            return;
          }
          this.copyFabricObject();
          this.$nextTick().then(() => {
            this.paste({
              x: this.currentObject.left + 10,
              y: this.currentObject.top + 10,
            });
          });
        },
      };
    },
    isEditing() {
      // Checks if the canvas is beign edited, fix for
      // add link modal and possible future issues
      // Couldnt add canvas editing here because of reactiveness
      return this.showLinkModal;
    },
  },
  watch: {
    showEditMenu(value) {
      if (value === true) {
        this.showEditMenuTouched = true; // Used when displaying side menu on iPad
      }
    },
    loading(newval) {
      // set loading cursor on canvas. Both lines are needed.
      document.getElementById(`editor-${this.uniqid}`).style.cursor = newval ? 'progress' : 'unset';
      this.canvas.defaultCursor = newval ? 'progress' : 'unset';
    },
  },
  beforeDestroy() {
    this.deactivatePaste();
    document.documentElement.style.overflow = 'inherit';
    window.removeEventListener('resize', this.onResize);

    this.$store.commit('preferences/setColor', '#000000');
  },
  mounted() {
    fabric.Object.NUM_FRACTION_DIGITS = 10;

    // Set default font
    if (this.fonts.length > 0) {
      this.font = this.fonts[0].font_family;
      this.font_display_name = this.fonts[0].display_name;
    }

    // Default size.
    this.sizePreset = this.sizePresets.find((preset) => preset.key === 'Normal');

    this.innerContent = this.content;

    // Touch device specific settings
    if (this.isTouchDevice) {
      fabric.Object.prototype.cornerSize = 40;
    }
    const vm = this;

    // Get active object from within hammer callback
    const getObject = () => {
      if (!vm.canvas.getActiveObject() && vm.lastObject) {
        vm.canvas.setActiveObject(vm.lastObject);
        vm.canvas.renderAll();
      }
      return vm.canvas.getActiveObject();
    };
    this.canvas = new FabricWithHammer(this.$refs.canvas, {
      preserveObjectStacking: true,
      fireRightClick: true,
      hammer: {
        // tap: (e) => {},
        pinchstart: (e) => {
          window.clearTimeout(vm.clearLastObjectTimeout);
          console.log('HAMMER: pinchstart', e);
          const object = getObject();
          vm.canvas.initialPinchScale = object ? object.scaleX : 1;
        },
        pinch: (e) => {
          // console.log("HAMMER: pinch")
          const object = getObject();
          if (object) {
            object.scale(e.scale * vm.canvas.initialPinchScale);
            vm.canvas.requestRenderAll();
          }
        },
        // pinchend: (e) => {},
        rotatestart: (e) => {
          console.log('HAMMER: rotatestart');
          const object = getObject();
          vm.canvas.initialRotateTheta = e.rotation - (object ? object.angle : 0);
          vm.canvas.initialRotateAngle = object ? object.angle : 0;
        },
        rotate: (e) => {
          const object = getObject();
          if (object) {
            const rotationTheta = e.rotation - vm.canvas.initialRotateTheta - vm.canvas.initialRotateAngle;
            if (Math.abs(rotationTheta) > 0) {
              const newRotationAngle = e.rotation - vm.canvas.initialRotateTheta;
              object.rotate(newRotationAngle);
            } else {
              object.rotate(vm.canvas.initialRotateAngle);
            }
            vm.canvas.requestRenderAll();
          } else {
            // console.log("NO OBJECT!!!", this.getActiveObject, this)
          }
        },
        press: (e) => {
          // console.log("HAMMER: press (longpress)")
          this.onLongPress({ ...e, clientY: e.center.y, clientX: e.center.x });
        },
        // pan: (e) => {},
        panstart: (e) => {
          console.log('HAMMER: panstart', e);
          // Avoid conflicting with fabricjs events
          // eslint-disable-next-line
          getObject()?.set({ evented: false });
        },
        panmove: (e) => {
          const object = getObject();
          if (!object) return;
          object.set({ left: e.center.x, top: e.center.y });
          vm.canvas.requestRenderAll();
        },
        panend: (e) => {
          console.log('HAMMER: panend', e);
          // eslint-disable-next-line
          getObject()?.set({ evented: true });
        },
      },
    });
    this.canvas.controlsAboveOverlay = true;
    //
    // *attempt* to make the Fabric.js canvas responsive;  based on:
    //    https://github.com/fabricjs/fabric.js/issues/3959
    //      (^ 'asturur' in this thread is one of fabric.js main committers!)
    //    https://stackoverflow.com/questions/21931271/
    //
    // const canvasWrapper = document.getElementById('seer-collage-fabric-canvas-wrapper');
    // const currZoomRatio = this.canvas.getWidth() / this.canvas.getHeight();
    // const width = canvasWrapper.clientWidth;
    // const dimensions = {
    //   width,
    //   height: width / currZoomRatio,
    // };
    // // this.canvas.setDimensions(dimensions);
    this.onResize();

    // console.log(`ImageRowComponent.vue:391  mounted()  currZoomRatio: ${currZoomRatio}  `
    //             + `dimensions: ${JSON.stringify(dimensions)}`);

    this.refresh();

    window.fabricjs = this.canvas;

    this.canvas.on('after:render', (evt) => {
      this.emitUpdate();
    });

    this.canvas.on('mouse:down', (evt) => {
      this.setPointer(evt);
      this.onSelectObject();
    });

    this.canvas.on('object:scaling', (evt) => {
      this.onScalingObject();
    });

    // // Note: This event works with fabric-with-gestures v4.4 (4.5 seems buggy)
    // this.canvas.on('touch:longpress', (evt) => {
    //   // We are filling the parameters required by vue-context
    //   // https://github.com/rawilk/vue-context/blob/14d4f12249979477be7314186eba493100591d5e/src/js/vue-context.js#L224
    //   this.onLongPress({ ...evt.e, clientY: evt.self.y, clientX: evt.self.x });
    // });

    this.canvas.on('object:added', (evt) => {
      this.$nextTick().then(() => {
        this.formatText(evt.target);
        this.onSelectObject();
        this.disableBadActions();
      });
    });

    this.canvas.on('text:editing:exited', (e) => {
      const obj = this.canvas.getActiveObject();
      if (obj.text === '') {
        obj.selectAll();
        obj.text = DEFAULT_TEXT;
        this.canvas.renderAll();
        obj.setCoords();
        this.canvas.renderAll();
      }

      this.isSmallCanvas = false;
    });

    if (this.isIPad) {
      this.canvas.on('text:editing:entered', (e) => {
        if (this.windowHeight < this.windowWidth) {
          this.isSmallCanvas = true;
          this.$nextTick().then(() => {
            setTimeout(() => {
              const canvasParent = this.$refs.canvas.parentNode;
              canvasParent.scrollTop = e.target.top - 80;
            }, 210);
          });
        }
      });
    }

    EventBus.$on('widget:added', () => {
      this.refresh();
    });

    this.activatePaste();

    this.canvas.selection = false;
    this.$on('contentUpdated', () => {
      // const editmenu = document.getElementById('editmenu')
      // this.showEditMenu = editmenu.getElementsByTagName('button').length > 0
    });
    this.blurhandler = () => {
      console.log('EMIT UPDATE');
      this.emitUpdate({ force_save: true });
    };
    window.addEventListener('blur', this.blurhandler);

    document.documentElement.style.overflow = 'hidden';

    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
  },
  unmounted() {
    window.removeEventListener('blur', this.blurhandler);
  },

  methods: {
    // V1 crop actions
    cropActions(action) {
      this.$refs.CropImageBoxTool[action]();
      this.$nextTick().then(() => {
        this.closeEditMenu();
      });
    },
    onCropFinished({ image: base64result, extra }) {
      this.showCropper = false;
      // Replace selected image with crop result
      this.deleteActive();
      this.addBase64Image(base64result, extra);
    },
    onResize() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
      let dimensions;
      let zoomratio;
      // console.log(this.containerSize);
      if (this.windowWidth > this.windowHeight) {
        zoomratio = this.containerSize.w / 800;
        dimensions = {
          width: this.containerSize.w,
          height: this.containerSize.h,
        };
      } else {
        zoomratio = this.containerSize.h / 650;
        dimensions = {
          width: this.containerSize.w,
          height: this.containerSize.h,
        };
      }
      this.canvas.setDimensions(dimensions);
      this.canvas.setZoom(zoomratio);
    },
    onLongPress(event) {
      if (!this.isIPad) {
        return;
      }
      if (this.mode === 'cropping') {
        return;
      }
      this.onShowContextMenu(event);
    },
    onShowContextMenu(event) {
      console.log('onShowContextMenu', event);
      const evt = event.srcEvent ? event.srcEvent : event;
      console.log('onShowContextMenu evt', evt);
      setTimeout(() => {
        this.$refs.rightClickMenu.open(evt, this.rightClickMenuItems());
      }, 300);
    },
    getDataURL() {
      try {
        if (this.canvas) {
          return this.canvas.toDataURL({
            format: 'png',
            multiplier: 2,
          });
        }
      } catch (error) {
        console.log('Failed to PNG render');
        console.log(error);
      }
      return null;
    },
    clickOutside() {
      this.closeEditMenu();
    },
    /** Warning: Seems unused. Consider deleting * */
    showBackgroundColorPicker() {
      // We need to set this Timeout since v-click-outside is being fired at same time
      setTimeout(() => {
        console.log('selectBackground');
        this.mode = 'selectBackground';
      }, 200);
    },
    resetMode(evt) {
      this.mode = '';
    },
    closeEditMenu() {
      this.showEditMenu = false;
    },
    changeSubValue(newVal) {
      this.submenu = newVal;
    },
    handleImageAnimated(event) {
      if (event && 'animated' in event) this.options.animated = event.animated;

      this.emitUpdate({
        force_save: event && 'force_save' in event ? event.force_save : undefined,
      });
    },
    emitUpdate(emitOptions = {}) {
      this.$emit('contentUpdated', {
        image: this.canvas.toSVG({ width: 800, height: 650 }),
        canvas: this.canvas.toJSON(['meta']),
        png_render: this.png_render,
        map: this.htmlmap(),
        options: this.options,
        emitOptions,
      });
    },
    imageAdded(image, meta) {
      //
      // 'meta' is an api.Collateral (e.g. an album image)
      // Q. how does this get triggered?  A. grep code for: emit.*imageAdded
      //
      image.meta = meta;
      delete image.meta.image_url;
      this.canvas.add(image);
      this.activateObject(image);
    },
    /** Replaces all current canvas content by the canvas received */
    canvasAdded(canvasContent) {
      this.canvas = this.canvas.loadFromJSON(canvasContent);
    },
    activateObject(newObj) {
      /** ⚠️ Important note
       * This is method is preventing multiple uploads on IPAD (Desktop works fine)
       * I couldnt figure out why but deactivating autoselect is bringing back multiple uploads
       *
       * Notes to whom is going fix this issue in the future:
       *  - After adding an image, the image tools will not be activated
       *  - It *seems* something related to Fabricjs with gestures adds Touch gestures to fabric
       *  - http://fabricjs.com/docs/fabric.Canvas.html#setActiveObject
       */
      if (this.isIPad) {
        return;
      }
      this.canvas.setActiveObject(newObj);
      this.canvas.fire('mouse:down');
    },
    formatText(newobject) {
      if (newobject.get('type') === 'i-text') {
        this.setSizePreset(this.sizePreset);
      }
      if (newobject.get('type') === 'textbox') {
        this.setSizePreset(this.sizePreset);
      }
    },
    disableBadActions() {
      this.canvas.getObjects().forEach((targ) => {
        if (targ.get('type') === 'image') { // !== 'react'
          // preserve aspect ratio
          targ.borderColor = '#007BFF';
          targ.cornerColor = '#007BFF';
          targ.cornerSize = 6;
          targ.setControlsVisibility({
            mt: false,
            mb: false,
            ml: false,
            mr: false,
          });
        }
        if (targ.get('type') === 'i-text') {
          targ.borderColor = '#007BFF';
          targ.cornerColor = '#007BFF';
          targ.cornerSize = 6;
          targ.setControlsVisibility({
            mt: false,
            mb: false,
          });
          // eslint-disable-next-line
          targ.lockScalingFlip = true;
          // eslint-disable-next-line
          targ.minScaleLimit = 0.1;
        }
        if (targ.get('type') === 'textbox') {
          targ.borderColor = '#007BFF';
          targ.cornerColor = '#007BFF';
          targ.cornerSize = 6;
          targ.setControlsVisibility({
            mt: false,
            mb: false,
          });
        }
      });
      this.canvas.renderAll();
    },
    moveSelection(x, y) {
      if (this.canvas) {
        this.canvas.getActiveObject().left += x;
        this.canvas.getActiveObject().top += y;
        this.canvas.renderAll();
      }
    },
    htmlmap() {
      const map = {
        size: { width: this.canvas.width, height: this.canvas.height },
        elements: [],
      };
      this.canvas.getObjects().filter((targ) => targ.get('type') === 'image').forEach((targ) => {
        let rect = targ.getBoundingRect();
        if (rect.top == null || rect.left == null || rect.width == null || rect.height == null) {
          // Skipping to address issue https://helloseer.atlassian.net/browse/SEER-107
          // not sure in what condition getBoundingRect will return a frame with null components
          // Skip for safety
          console.log('Skipping html frame because some bounding item is null', rect.top, rect.left, rect.width, rect.height);
          return;
        }
        const zoom = this.canvas.getZoom();
        rect = {
          left: rect.left / zoom,
          top: rect.top / zoom,
          width: rect.width / zoom,
          height: rect.height / zoom,
        };
        // const { meta } = targ
        if (targ.meta) {
          map.elements.push({ url: targ.meta.site_url, sync_id: targ.meta.sync_id, frame: rect });
        }
      });
      return map;
    },
    testsomething() {
      this.htmlmap();
      /*
      console.debug(this.canvas)
      this.canvas.getObjects().forEach( targ => {
          // Create the SV
          console.debug("============")
          console.debug(targ.meta)
          console.debug(targ.getBoundingRect())
          console.debug(targ['scaleX'], targ.scaleY)
          console.debug(this.canvas.toJSON(['meta']))
      }) 7747
      */
    },
    handleUpdateAddImageIsOpen(value) {
      this.addImageIsOpen = value;
    },
    changeMode(mode) {
      console.log('Mode', mode);
      this.mode = mode;
    },
    async pasteImage(base64) {
      this.addBase64Image(base64);

      // this.addS3Image(base64);
    },
    addS3Image(base64) {
      api.post('/upload_media_to_s3', { url: base64 }).then((response) => {
        const newUrl = response.data.upload_url;
        console.log('Image uploaded to ', newUrl);

        // eslint-disable-next-line no-new
        new fabric.Image.fromURL(newUrl, (image) => {
          console.log('uploaded', this.canvas.width);
          const imageAspect = image.width / image.height;

          image.setControlsVisibility({
            mt: false, // middle top disable
            mb: false, // midle bottom
            ml: false, // middle left
            mr: false, // I think you get it
          });

          if (imageAspect > 1.0) {
            // wide image
            image.scaleToWidth(0.9 * this.canvas.width);
            // image.width = 0.9 * this.canvas.width
            // image.height = image.width / imageAspect
          } else {
            image.scaleToHeight(0.9 * this.canvas.height);
            // image.height = 0.9 * this.canvas.height
            // image.width = image.height * imageAspect
          }

          console.log('uploaded', this.canvas.width);
          image.set({
            originX: 'center',
            originY: 'center',
            left: this.canvas.width / 2,
            top: this.canvas.height / 2,
          });

          this.canvas.add(image);
          // .setActiveObject(image);
          this.activateObject(image);
          this.showEditMenu = true;
          this.canvas.renderAll();
        }, { crossOrigin: 'anonymous' });
      }).catch((error) => {
        console.error('Something went wrong!', error);
        EventBus.$emit('toast-error', 'Failed to upload image. Please retry in a few moments');
      });
    },
    uploadToS3AndReplace(base64, image, width, height, productMetadata = null) {
      // console.debug('uploadToS3AndReplace()');
      // console.table(width, height);

      /*
         Send given image 'base64' to storage/S3, "replacing" a placeholder
         fabric.Image with one whose source is the remote storage.

         (10/7/21  note that 'base64' *actually* appears to be a string URL to an image)
       */
      const PARAM_PRODUCT_METADATA = 'product'; // "productMetadata" more greppable name?
      const postData = {
        url: base64,
      };
      if (productMetadata) {
        postData[PARAM_PRODUCT_METADATA] = productMetadata;
      }

      api.post('/upload_media_to_s3', postData).then((response) => {
        const s3url = response.data.upload_url;

        // Construct an HTMLImageElement to load an image "off-screen"
        // that is then copied over to a fabric.Image instance for use in the editor.
        // Needed for loading the image without blinking
        const img = new Image();
        img.src = s3url;
        img.onload = (i) => {
          image.setSrc(s3url, (remoteImage) => {
            remoteImage.set({
              left: image.left,
              scaleX: (width * remoteImage.scaleX) / image.width,
              scaleY: (height * remoteImage.scaleY) / image.height,
              dirty: true,
            });
            remoteImage.setCoords();

            // Needed for redrawing the canvas
            setTimeout(() => {
              this.canvas.renderAll();
            }, 100);
          }, { crossOrigin: 'anonymous' });
        };
      }).catch((error) => {
        console.log('Error', error);
        EventBus.$emit('toast-error', 'Failed to upload image. Please retry in a few moments');
      });
    },
    addBase64Image(base64, extraFabricImageProps = {}, productMetadata = null) {
      // XXX  note that 'base64' can also be a url to an image,
      //      and not neccessarily the bytes of an encoded base64 image
      //
      // XXX  'productImageSelectionRecord' is an optional reference to an
      // INLINE dict/object instantiated in the select() method of ProductSelectorFetch.vue
      //
      // eslint-disable-next-line no-new
      new fabric.Image.fromURL(base64, (image, hadError) => {
        // http://fabricjs.com/docs/fabric.Image.html#.fromURL

        const zoom = this.canvas.getZoom();
        console.log('addBase64Image 2');
        const imageAspect = image.width / image.height;
        image.setControlsVisibility({
          mt: false, // middle top disable
          mb: false, // midle bottom
          ml: false, // middle left
          mr: false, // I think you get it
        });

        if (imageAspect > 1.0) {
          // wide image
          image.scaleToWidth(0.9 * (this.canvas.width / zoom));
          // image.width = 0.9 * this.canvas.width
          // image.height = image.width / imageAspect
        } else {
          image.scaleToHeight(0.9 * (this.canvas.height / zoom));
          // image.height = 0.9 * this.canvas.height
          // image.width = image.height * imageAspect
        }

        // XXX fml also see EditSection.parse_canvas_content() in
        // <stylelab-backend>/api/models.py:2067
        let veryMagicalProductMetaDict = {};

        if (productMetadata) {
          veryMagicalProductMetaDict = { // MAGIC_PRODUCT_METADATA
            image_url: productMetadata.image_url,
            site_url: productMetadata.site_url,
            product: productMetadata,
          };
        }
        image.set({
          originX: 'center',
          originY: 'center',
          left: this.canvas.width / (2 * zoom),
          top: this.canvas.height / (2 * zoom),

          meta: veryMagicalProductMetaDict,
          ...extraFabricImageProps,
        });

        this.canvas.add(image);
        this.activateObject(image);
        this.showEditMenu = true;
        this.canvas.renderAll();

        // Uploading to S3 and replacing the image
        const { width, height } = image;
        console.table(width, height);
        this.uploadToS3AndReplace(base64, image, width, height, productMetadata);
      });
    },
    setPointer(evt) {
      if (!evt.e) {
        return;
      }
      this.pointer = this.canvas.getPointer(evt.e);
    },
    onSelectObject() {
      const delta = new Date() - this.lastSelectionTime;
      this.lastSelectionTime = new Date();
      const object = this.canvas.getActiveObject();
      this.$nextTick().then(() => {
        this.$refs.rightClickMenu.close();
      });
      if (!object) {
        this.showEditMenu = false;
        console.log('DESELECTED OBJECT!!');
        this.clearLastObjectTimeout = window.setTimeout(() => {
          console.log('DELETING LAST OBJECT REFERENCE');
          this.canvas.discardActiveObject().renderAll();
          this.lastObject = null;
        }, 100);
        return;
      }
      this.lastObject = this.canvas.getActiveObject();

      if (this.next_select) {
        // console.debug('Stop select object because of next_select')
        this.next_select(this.canvas.getActiveObject());
        this.next_select = null;
        return;
      }
      // console.debug(this.lastFontSize)
      this.currentObject = object;
      // console.debug("Font is", object)
      if (object.get('fontSize')) this.lastFontSize = object.get('fontSize') * object.get('scaleX');
      // console.debug(this.lastFontSize)
      if (object != null) {
        // Get colors
        this.setBorder(object.stroke);
        this.setFill(object.fill);

        // Get fonts
        if (object.fontFamily) {
          this.font = object.fontFamily;
        }
        if (object.displayName) {
          this.font_display_name = object.displayName;
        }
      }

      if (object && object.get('fontSize')) {
        // eslint-disable-next-line
        const preset = this.sizePresets.find((preset) => (object.get('scaleX') === 1.0) && preset.value === object.get('fontSize')) || this.sizePresets.find((preset) => preset.key === 'Custom')
        this.sizePreset = preset;
        this.clearText(object);
      }
      this.showEditMenu = true;

      if (object.type === 'i-text' && object.isEditing && delta < 250) {
        this.$nextTick().then(() => {
          object.selectAll();
        });
      }
    },
    onDeselectObject() {},
    onScalingObject() {
      this.sizePreset = this.sizePresets.find((preset) => preset.key === 'Custom');
    },
    refresh() {
      this.$nextTick().then(() => {
        this.canvas.clear();
        this.canvas.backgroundColor = 'white';
        this.canvas.renderAll();

        if (this.content) {
          this.canvas.loadFromJSON(this.content.canvas, () => {
            this.canvas.renderAll();
          });
        }
      });
    },
    setBorder(color) {
      this.strokeColor = color;
      this.canvas.freeDrawingBrush.color = color;

      if (this.canvas.getActiveObject()) {
        this.canvas.getActiveObject().set('stroke', color);
      }
      this.canvas.renderAll();
    },
    setFill(color) {
      this.fillColor = color || '';
      if (this.canvas.getActiveObject()) {
        this.canvas.getActiveObject().set('fill', color);
      }
      this.canvas.renderAll();
    },
    setSizePreset(preset) {
      this.sizePreset = preset;
      if (this.canvas.getActiveObject()) {
        this.canvas.getActiveObject().set('scaleX', 1);
        this.canvas.getActiveObject().set('scaleY', 1);
        this.canvas.getActiveObject().set('fontSize', preset.value || this.lastFontSize || 11);
      }
      this.canvas.renderAll();
    },
    setFont(font, displayName) {
      this.font = font;
      this.font_display_name = displayName;
      if (this.canvas.getActiveObject()) {
        this.canvas.getActiveObject().set('fontFamily', font);
        this.canvas.getActiveObject().set('displayName', displayName);
      }
      this.canvas.renderAll();
    },
    keymonitor(event) {
      // console.debug(event.key);
    },
    deleteActive() {
      // eslint-disable-next-line
      this.canvas.getActiveObject()?.remove();
    },
    html() {
      return this.canvas.toSVG({ width: 800, height: 650 });
    },
    toggleDrawingMode() {
      this.canvas.freeDrawingBrush.color = this.strokeColor;

      if (this.canvas.isDrawingMode === true) {
        this.isDrawingMode = false;
        this.canvas.isDrawingMode = false;
      } else {
        this.isDrawingMode = true;
        this.canvas.isDrawingMode = true;
      }
    },
    resetSelections() {
      this.closeEditMenu();
      this.canvas.discardActiveObject();
      this.canvas.renderAll();
    },
    onClose() {
      this.$nextTick().then(() => {
        this.png_render = this.getDataURL();
        this.emitUpdate();
        console.log('this.$refs', this.$refs);
        if (this.$refs.CropImageBoxTool) this.$refs.CropImageBoxTool.cancel();
        this.$emit('close');
      });
    },
    onLinkModalChanged(newval) {
      this.showLinkModal = newval;
    },

    clearText(object) {
      if (object.type === 'i-text') {
        if (!object.cleared && object.text === this.default_text) {
          object.cleared = true;
          object.selectAll();
          // object.text = '';
          object.dirty = true;
          object.setCoords();
          this.canvas.renderAll();
          object.enterEditing();
          object.selectAll();
        }
      }
    },
    handlePaste() {
      // if()
      this.paste();
    },
  },
};
</script>

<style lang="scss" scoped>
.footer {
  font-size: 15px !important;
  &--disabled {
    opacity: 0.4;
    pointer-events: none;
  }
}
.close-button-container {
  position: relative;
  z-index: $zindex-modal;
  background-color: #FAF9F7;
  width:100%;
  float:left;
  border-radius: 5px 5px 0 0;
  user-select: none;
  &--disabled > div {
    opacity: 0.4;
    pointer-events: none;
  }
}
.close-button {
  cursor: pointer;
  transition: opacity .3s ease;
  padding: 10px 10px 10px 5px;
  &:hover {
    opacity: 0.4;
  }
}
.overlay {
  background-color: rgba(0,0,0,0.6);
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  &.loading {
    cursor: progress;
  }
  .main-layout {
    background-color: transparent !important;
  }
  transition: background-color .5s ease;
}
.image-box-tool {
  transition: opacity .25s ease;
}
.editmenu {

  transition: top 0.2s ease;

  &--disabled {
    > .image-box-tool {
      opacity: 0.4;
    }
  }
  &--small {
    top: 0;
  }
}
</style>

<style lang="scss">
@import '~vue-context/dist/css/vue-context.css';
.v-context{
  background-color: inherit !important;
  // box-shadow: inherit !important;
  padding: inherit !important;
  border: inherit !important;
  &:hover {
    background-color: inherit !important;
  }
  overflow: hidden;
  & button {
    text-align: left;
  }
}

.image-row-container .main-layout__container{
  margin-left: auto;
  margin-right: auto;
  min-width: auto;
  max-width: 100%;
  align-self: self-start;
}
.image-row-container .basewidget{
  flex-direction: row;
}

.overlay .main-layout {
  background-color: transparent !important;
}
.basewidget {
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 0 !important;
  .basecard{
    border-radius: 0 0 5px 5px;
  }
}

.basewidget .basecard .card-body {
  padding: 0 !important;
}

.basewidget .basecard .card-body .footer {
  margin: 0 !important;
  padding: 0 0 0 0.5rem;
}

.lower-canvas {
  background-color: rgba(255, 255, 255, 0.3);
}

.contextual-menu {
  background: $gray-light;
  font-size: 13px;
  padding: 12px;
  z-index: $zindex-modal;
  border-radius: 8px;
  &.submenu {
    right: -12px;
    transform: translateX(100%);
    position: absolute;
    top: 0;
    border-top-left-radius: 0px;
    padding-top: 0px;
    overflow: hidden;
  }
}

.image-row__actions {
  position: absolute;
  top: 50%;
  width: 195px;
  margin-left: -200px;
}
.v-context.image-row__actions{
  margin-left: 0;
}

.image-row {
  &__toolbar {
    background: white;
    left: 0;
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: $zindex-modal;
  }

  &__button {
    color: black;
    background: none;
    border: none;
    padding: 0.75rem 0.5rem;
    cursor: pointer;
    opacity: 0.8;
    transition: all 0.3s;
    width: 100%;

    &:hover {
      opacity: 1;
      background-color: #F2F1EE;
      &.submenubutton {
        width: calc(100% + 24px);
        margin-left: -12px;
        padding-left: 20px;
      }
    }

    &.--disabled {
      opacity: 0.1;
    }

    &--active {
      opacity: 1;
    }

    &--disabled {
      pointer-events: none;
      opacity: 0.1;
    }

    &--color {
      padding: 0;
    }
  }
}

.canvaswrap div > canvas {
  transition: height 0.2s;
}

/*
.canvaswrap div {
  transition: height 0.2s;
}
*/

.canvaswrap--small.canvaswrap {
  height: unset !important;
}

.canvaswrap--small div{
  height: 300px !important;
  overflow-x: hidden;
}
  /** Removed since we have bottom toolbar */
  // .is-ipad .collage-canvas {
  //   transition: transform 0.2s;
  //   &--open{
  //     @media only screen and (max-width: 1366px) and (orientation: landscape){
  //       // transform: translateX(100px);
  //     }
  //   }
  // }
// Dropable zone from Chrome extension
.drop-active{
  // This can be set is we want an special style when drag & dropping from chrome extension
  // outline: solid 1px $clementine;
}
.loading_spinner{
  padding: 10px;
  display: inline-block;
  border-radius: 5px;
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  z-index: 1;
  justify-content: center;
  align-items: center;
  animation-name: fadeInOpacity;
  animation-iteration-count: 1;
  animation-timing-function: ease-in;
  animation-duration: 7s;
  @keyframes fadeInOpacity {
    10% {
      background-color: rgba(0, 0, 0, 0.60)
    }
    100% {
      background-color: rgba(0, 0, 0, 0.10)
    }
  }
}
</style>
